import { ProjectCourseFlagEnum } from '@/models/Flag';
import Hackathon, {
  HackathonStage,
  StageTypeEnum,
  Winner,
} from '@/models/Hackathon';
import { minBy } from 'lodash';
import moment from 'moment';
import { setEndDate, setStartDate } from './datetimes';
import { Project } from '@/models/Project';
import { FlagTypeEnum } from '@/models/Flag';
import { UserTypeEnum } from '@/models/User';
import { isSuperAdmin } from '@/functions/auth';

export function getCurrentStage(
  stages: HackathonStage[],
): HackathonStage | undefined {
  const now = moment();
  return (
    stages.find(stage => {
      const startDate = moment(stage.startDate);
      const endDate = moment(stage.endDate);
      return now.isBetween(startDate, endDate, null, '()');
    }) || undefined
  );
}

export function getStageByType(
  stages: HackathonStage[],
  type: StageTypeEnum,
): HackathonStage | undefined {
  return stages.find(stage => stage.type === type);
}

type ShouldHideParticipantsParams = {
  stages: HackathonStage[];
  isLoadingStages: boolean;
  userType: UserTypeEnum | undefined;
};

export function shouldHideParticipants({
  stages,
  isLoadingStages,
  userType,
}: ShouldHideParticipantsParams): boolean {
  if (isSuperAdmin(userType)) return false;
  if (isLoadingStages) return true;
  const hideStages = [StageTypeEnum.SUBMISSION, StageTypeEnum.INITIAL_VOTING];
  return stages.some(
    stage =>
      hideStages.includes(stage.type) && moment().isSameOrBefore(stage.endDate),
  );
}

export function hideParticipants(project: Project): Project {
  return { ...project, users: [], units: [], teacherMentor: undefined };
}

export function getNextStage(
  stages: HackathonStage[],
): HackathonStage | undefined {
  const now = moment();
  const distances = stages.map(stage => ({
    stage,
    startsOn: moment(stage.startDate).diff(now),
  }));
  const distancesPositives = distances.filter(
    distance => distance.startsOn >= 0,
  );
  const nextStage = minBy(distancesPositives, 'startsOn');
  return nextStage?.stage;
}

export function getLastStage(stages: HackathonStage[]) {
  const lastStage = stages.reduce(
    (latest, stage) =>
      moment(stage.endDate).isAfter(moment(latest?.endDate)) ? stage : latest,
    stages.at(0),
  );

  return lastStage;
}

export function getLastCompletedStage(stages: HackathonStage[]) {
  const temporalSortedStages = stages.sort(
    (a, b) => moment(b.endDate).valueOf() - moment(a.endDate).valueOf(),
  );
  for (const stage of temporalSortedStages)
    if (getStageStatus(stage) === 'complete') return stage;

  return undefined;
}

type stageStatus = 'future' | 'complete' | 'progress';

export function getStageStatus(stage: HackathonStage): stageStatus {
  const now = moment();
  const startDate = moment(stage.startDate);
  const endDate = moment(stage.endDate);

  if (now.isBefore(startDate)) {
    return 'future';
  }
  if (now.isAfter(endDate)) {
    return 'complete';
  }
  return 'progress';
}

export const getWinnersCount = () =>
  Object.values(ProjectCourseFlagEnum).length * 3;

export const getWinnerByProjectId = (
  projectId: number,
  winners: Winner[] = [],
) => {
  return winners.find(winner => winner.project.id === projectId);
};

export const getWinnerByRank = (rank: number, winners: Winner[] = []) => {
  return winners.find(winner => winner.rank === rank);
};

export const classNamePodium: Record<number, string> = {
  1: 'border-[#FDBB4C] shadow-[#FDBB4C]/50',
  2: 'border-[#C1C7D4] shadow-[#C1C7D4]/50',
  3: 'border-[#CD7F32] shadow-[#CD7F32]/50',
  4: '',
};

export const createNextStage = (
  stage: HackathonStage,
): Omit<HackathonStage, 'id'> => {
  const stagesSequence: StageTypeEnum[] = [
    StageTypeEnum.SUBMISSION,
    StageTypeEnum.INITIAL_VOTING,
    StageTypeEnum.FINAL_VOTING,
  ];

  const nextStage = {
    startDate: setStartDate(
      moment(stage?.endDate).add({ day: 1 }).toISOString(),
    ),
    endDate: setEndDate(moment(stage?.endDate).add({ days: 15 }).toISOString()),
    type: stagesSequence[
      (stagesSequence.findIndex(s => s === stage.type) + 1) % 3
    ],
    hackathonId: stage.hackathonId,
  };
  return nextStage;
};

export const createDefaultStage = (hackathon: Hackathon) => {
  const startDate = moment();

  const defaultNext: Omit<HackathonStage, 'id'> = {
    startDate: setStartDate(startDate.toISOString()),
    endDate: setEndDate(startDate.add({ days: 15 }).toISOString()),
    type: StageTypeEnum.SUBMISSION,
    hackathonId: hackathon.id,
  };
  return defaultNext;
};

export function hasFinalistTag(project: Project) {
  return !!project.flags?.some(flag => flag.type === FlagTypeEnum.FINALIST);
}

export const addLiveStreamUrl = (hackathon?: Hackathon) => {
  if (!hackathon) return;
  return {
    ...hackathon,
    liveStreamUrl: 'https://www.youtube.com/live/BqSnkjl6oaM',
  };
};
